const CapabilityRegistry = require('./capabilities/CapabilityRegistry');

// Helper function to make authenticated API calls (inline to avoid ESM/CJS interop issues)
async function makeAuthenticatedRequest(path, options = {}) {
  const token = typeof window !== "undefined" && typeof localStorage !== "undefined" 
    ? localStorage.getItem("token") 
    : null;
  
  if (!token) {
    console.warn("No token available for authenticated request");
    return null;
  }

  const url = path.startsWith("/api/") ? path : `/api${path.startsWith("/") ? path : "/" + path}`;
  
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "Authorization": `Bearer ${token}`,
      ...(options.headers || {})
    },
    credentials: "include"
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status} ${response.statusText}`);
  }

  return response.json();
}

class ComponentActionHandler {
  constructor(planStateSetters, planGetters, savePlan) {
    this.setters = planStateSetters;
    this.getters = planGetters;
    this.savePlan = savePlan;

    // Initialize capability registry
    this.capabilityRegistry = new CapabilityRegistry(planStateSetters, planGetters, savePlan);

    // Get direct reference to PlanOperationsCapability for strategy component updates
    this.planOperations = this.capabilityRegistry.getPlanOperationsInstance();

    console.log("🎯 ComponentActionHandler: Initialized with modular capabilities");
  }

  // Validate if action can be executed based on agent capabilities
  async validateCapability(agentId, capabilityId) {
    try {
      const response = await fetch(`/api/agents/validate/${agentId}/${capabilityId}`);
      const data = await response.json();
      return data.can_execute;
    } catch (error) {
      console.warn(`Capability validation failed for ${agentId}:${capabilityId}`, error);
      return false; // Fail safe
    }
  }

  async handleAction(actionType, component, data) {
    // Handle both old object format and new parameter format
    const action = typeof actionType === "object" 
      ? actionType 
      : { type: actionType, component, data };

    console.log("ComponentActionHandler received action:", action);

    // Handle direct AI capability responses (when action has a 'capability' field)
    if (action.capability && !action.type) {
      console.log("🔧 Processing direct AI capability:", action.capability);
      console.log("🔧 Full action structure:", JSON.stringify(action, null, 2));

      const capability = action.capability;

      // CRITICAL: Validate capability exists before execution
      if (!this.capabilityRegistry.hasCapability(capability)) {
        console.error(`❌ CRITICAL: Unknown capability '${capability}'`);
        console.error(`❌ Available capabilities:`, this.capabilityRegistry.getAvailableCapabilities());
        return { success: false, message: `Unknown capability: ${capability}. Available: ${this.capabilityRegistry.getAvailableCapabilities().join(', ')}` };
      }

      try {
        console.log(`✅ Executing validated capability: ${capability}`);
        const actionContext = action.context || {};
        const result = await this.capabilityRegistry.executeCapability(
          capability, 
          action.data,
          action,
          actionContext
        );

        // Trigger UI refresh for strategy component updates
        if (capability.includes('update_') && result.success) {
          console.log("🎨 Strategy component updated, triggering UI refresh");
          // Force a React state update cycle
          setTimeout(() => {
            console.log("🔄 UI refresh cycle completed");
          }, 150);
        }

        return result;
      } catch (error) {
        console.error(`❌ Error executing direct AI capability ${capability}:`, error);
        return { success: false, message: `Error executing ${capability}: ${error.message}` };
      }
    }

    // Handle component_capability wrapper format
    if (action.type === "component_capability") {
      // Extract capability from different possible locations in the action structure
      const capability = action.capability || action.data?.capability || 
                        (typeof action.data === 'object' && !Array.isArray(action.data) ? 
                         Object.keys(action.data)[0] : null);

      console.log("🔧 Processing component_capability:", capability);
      console.log("🔧 Full action structure:", JSON.stringify(action, null, 2));

      if (!capability) {
        console.error("❌ No capability found in action:", action);
        return { success: false, message: "No capability specified in action" };
      }

      // Use capability registry to handle the action
      if (this.capabilityRegistry.hasCapability(capability)) {
        try {
          const actionContext = action.context || {};
          const result = await this.capabilityRegistry.executeCapability(
            capability, 
            action.data?.data || action.data,
            action,
            actionContext
          );
          return result;
        } catch (error) {
          console.error(`❌ Error executing capability ${capability}:`, error);
          return { success: false, message: `Error executing ${capability}: ${error.message}` };
        }
      } else {
        console.warn("Unknown component capability:", capability);
        return { success: false, message: `Unknown capability: ${capability}` };
      }
    }

    // Handle legacy action types - route them to appropriate capabilities
    const legacyActionMap = {
      "update_summary": "update_summary",
      "update_problem_statement": "update_problem_statement",
      "update_goal_statement": "update_goal_statement",
      "update_target_statement": "update_target_statement",
      "update_objectives": "update_objectives",
      "update_deliverables": "update_deliverables",
      "update_metrics": "update_metrics",
      "update_stakeholders": "update_stakeholders",
      "update_design_challenge": "update_design_challenge",
      "update_programs": "update_programs",
      "update_strategy_component": "update_strategy_component",
      "tool_duration_editing": "tool_duration_editing",
      "remove_tools_from_stages": "remove_tools_from_stages",
      "add_tools_to_stages": "add_tools_to_stages",
      "modify_tools_content": "modify_tools_content",
      "enable_feature": "enable_feature",
      "update_strategic_themes": "update_strategic_themes",
      "update_design_vision": "update_design_vision",
      "update_audience_insights": "update_audience_insights",
      "update_opportunity_areas": "update_opportunity_areas",
      "update_experience_design_principles": "update_experience_design_principles",
      "update_design_hypotheses": "update_design_hypotheses",
      "update_brief_deconstruction": "update_brief_deconstruction",
      "update_information_assessment": "update_information_assessment",
      "update_strategic_guidance": "update_strategic_guidance",
      "update_success_metrics": "update_success_metrics",
      "update_unmet_needs": "update_unmet_needs",
      "update_competitive_ux_positioning": "update_competitive_ux_positioning",
      "update_key_objectives": "update_key_objectives",
      "update_market_intelligence": "update_market_intelligence",
      "update_opportunity_analysis": "update_opportunity_analysis",
      "update_methodology": "update_methodology",
      "bulk_actions": "bulk_actions",
      "content_editing": "content_editing",
      "program_content_modification": "program_content_modification",
      "bulk_content_operations": "bulk_content_operations",
      "ai_content_generation": "ai_content_generation",
      "generate_plan_cta": "generate_plan_cta",
      "refine_brief": "refine_brief",
      "suggest_approach": "suggest_approach"
    };

    const capabilityName = legacyActionMap[action.type];
    if (capabilityName && this.capabilityRegistry.hasCapability(capabilityName)) {
      try {
        const actionContext = action.context || {};
        return await this.capabilityRegistry.executeCapability(capabilityName, action.data, action, actionContext);
      } catch (error) {
        console.error(`❌ Error executing legacy action ${action.type}:`, error);
        return { success: false, message: `Error executing ${action.type}: ${error.message}` };
      }
    }

    // Special handling for strategy creation complete (keeping this in main handler for now)
    if (action.type === "strategy_creation_complete") {
      return this.handleStrategyCreationComplete(action.data);
    }

    //New code added here
    if (action.type === "update_strategy_component") {
      return this.handleUpdateStrategyComponent(action.data);
    }

    console.warn("Unknown action type:", action.type);
    return { success: false, message: `Unknown action type: ${action.type}` };
  }

  async handleStrategyCreationComplete(data) {
    console.log("🎉🎉🎉 COMPLETION PATH TRACE [5/5]: ComponentActionHandler.handleStrategyCreationComplete called");
    console.log("Strategy creation complete, processing enhanced data:", data);
    
    // DEBUG: Check if onSaveComplete callback is present
    console.log("🔍 DEBUG: Callback check", {
      hasOnSaveComplete: typeof data?.onSaveComplete === 'function',
      onSaveCompleteType: typeof data?.onSaveComplete,
      hasOnSaveError: typeof data?.onSaveError === 'function',
      dataKeys: data ? Object.keys(data) : []
    });

    // CRITICAL: Log the exact data received from workflow
    console.log("WORKFLOW COMPLETION DATA RECEIVED:", {
      hasData: !!data,
      dataKeys: data ? Object.keys(data) : [],
      hasPrograms: !!data?.finalProgramSequence,
      programsCount: data?.finalProgramSequence?.length || 0,
      hasSelectedPrograms: !!data?.selectedPrograms,
      selectedProgramsCount: data?.selectedPrograms?.length || 0,
      hasShortSummary: !!data?.shortSummary,
      hasGoalStatement: !!data?.goalStatement,
      hasProblemStatement: !!data?.problemStatement,
      hasTargetStatement: !!data?.targetStatement,
      hasObjectives: !!data?.objectives,
      hasDeliverables: !!data?.deliverables,
      hasMetrics: !!data?.metrics,
      hasStakeholders: !!data?.stakeholders,
      totalDuration: data?.totalDuration,
      // CRITICAL: Add detailed program logging for ALL possible program fields
      finalProgramSequencePreview: data?.finalProgramSequence?.slice(0, 2).map(p => ({
        name: p?.program_of_work || p?.name || p?.title,
        hasToolsByStage: !!p?.toolsByStage,
        toolStageCount: p?.toolsByStage ? Object.keys(p.toolsByStage).length : 0
      })),
      selectedProgramsPreview: data?.selectedPrograms?.slice(0, 2).map(p => ({
        name: p?.program_of_work || p?.name || p?.title,
        hasToolsByStage: !!p?.toolsByStage,
        toolStageCount: p?.toolsByStage ? Object.keys(p.toolsByStage).length : 0
      }))
    });

    // CRITICAL FIX: Extract ALL workflow data, not just programs
    console.log("🔍 Complete workflow data received:", {
      topLevelKeys: Object.keys(data || {}),
      hasShortSummary: !!data?.shortSummary,
      hasProblemStatement: !!data?.problemStatement,
      hasGoalStatement: !!data?.goalStatement,
      hasTargetStatement: !!data?.targetStatement,
      hasObjectives: !!data?.objectives,
      hasDeliverables: !!data?.deliverables,
      hasMetrics: !!data?.metrics,
      hasStakeholders: !!data?.stakeholders,
      workflowDataSample: JSON.stringify(data, null, 2).substring(0, 500)
    });

    // Extract strategy content data with comprehensive fallbacks
    const strategyData = {
      planName: data?.planName || data?.plan_name || "",
      shortSummary: data?.shortSummary || data?.short_summary || data?.shortsummary || data?.summary || "",
      problemStatement: data?.problemStatement || data?.problem_statement || data?.problem || "",
      goalStatement: data?.goalStatement || data?.goal_statement || data?.goal || "",
      targetStatement: data?.targetStatement || data?.target_statement || data?.target || "",
      objectives: data?.objectives || data?.objectivesStatement || [],
      deliverables: data?.deliverables || data?.deliverablesStatement || "",
      metrics: data?.metrics || data?.metricsStatement || "",
      stakeholders: data?.stakeholders || data?.stakeholdersCustomersInvolvement || [],
      totalDuration: data?.totalDuration || 0,
      experienceDesignPrinciples: data?.experienceDesignPrinciples || data?.experience_design_principles || ""
    };

    console.log("🔍 Extracted strategy data:", {
      hasShortSummary: !!strategyData.shortSummary,
      shortSummaryLength: strategyData.shortSummary?.length || 0,
      hasProblemStatement: !!strategyData.problemStatement,
      problemStatementLength: strategyData.problemStatement?.length || 0,
      hasGoalStatement: !!strategyData.goalStatement,
      goalStatementLength: strategyData.goalStatement?.length || 0,
      hasTargetStatement: !!strategyData.targetStatement,
      targetStatementLength: strategyData.targetStatement?.length || 0,
      objectivesCount: Array.isArray(strategyData.objectives) ? strategyData.objectives.length : 0,
      deliverablesLength: strategyData.deliverables?.length || 0,
      metricsLength: strategyData.metrics?.length || 0,
      stakeholdersCount: Array.isArray(strategyData.stakeholders) ? strategyData.stakeholders.length : 0,
      hasExperienceDesignPrinciples: !!strategyData.experienceDesignPrinciples,
      experienceDesignPrinciplesLength: strategyData.experienceDesignPrinciples?.length || 0
    });

    // CRITICAL FIX: Be more comprehensive about program data extraction
    let programs = [];

    // Try multiple data extraction strategies in order of preference
    if (data?.finalProgramSequence && Array.isArray(data.finalProgramSequence) && data.finalProgramSequence.length > 0) {
      programs = data.finalProgramSequence;
      console.log("✅ Using finalProgramSequence:", programs.length, "programs");
    } else if (data?.selectedPrograms && Array.isArray(data.selectedPrograms) && data.selectedPrograms.length > 0) {
      programs = data.selectedPrograms;
      console.log("✅ Fallback to selectedPrograms:", programs.length, "programs");
    } else if (data?.programs && Array.isArray(data.programs) && data.programs.length > 0) {
      programs = data.programs;
      console.log("✅ Fallback to programs:", programs.length, "programs");
    } else {
      // CRITICAL: Check if data has nested workflow results
      if (data?.workflowResults?.programs?.finalProgramSequence && Array.isArray(data.workflowResults.programs.finalProgramSequence)) {
        programs = data.workflowResults.programs.finalProgramSequence;
        console.log("✅ Found programs in nested workflowResults:", programs.length, "programs");
      } else if (data?.uiUpdate?.finalProgramSequence && Array.isArray(data.uiUpdate.finalProgramSequence)) {
        programs = data.uiUpdate.finalProgramSequence;
        console.log("✅ Found programs in uiUpdate:", programs.length, "programs");
      } else {
        console.error("❌ No valid program data found in workflow results");
        console.error("Available data keys:", Object.keys(data || {}));
        console.error("Nested data exploration:", {
          hasWorkflowResults: !!data?.workflowResults,
          workflowResultsKeys: data?.workflowResults ? Object.keys(data.workflowResults) : [],
          hasUiUpdate: !!data?.uiUpdate,
          uiUpdateKeys: data?.uiUpdate ? Object.keys(data.uiUpdate) : []
        });
        console.error("Raw data sample:", JSON.stringify(data, null, 2).substring(0, 1000));
      }
    }

    // Validate we have programs - UNLESS custom_programs step was intentionally skipped (Decision Call)
    const customProgramsSkipped = data?.workflowMetadata?.executionPlan?.customProgramsSkipped || false;
    
    if (!programs || programs.length === 0) {
      // Check if this is expected behavior (Decision Call mode)
      if (customProgramsSkipped) {
        console.log("✅ Decision Call mode: custom_programs step was skipped intentionally, proceeding with strategy-only save");
        programs = []; // Set empty array for consistency
      } else {
        // Full Strategy mode - programs are required
        console.error("❌ No programs found after extraction attempts (and custom_programs was not skipped)");
        this.setters.setIsCreatingStrategy(false);
        this.setters.setIsStrategyCreationMode(false);
        if (data?.onSaveError) {
          data.onSaveError(new Error("No programs received from workflow"));
        }
        return;
      }
    }

    try {
      // Use the extracted programs

      // CRITICAL: Validate program structure before setting state
      // For Decision Call, skip validation if no programs
      const validPrograms = customProgramsSkipped && programs.length === 0 
        ? [] 
        : programs.filter((program) => {
            const isValid = program && typeof program === "object" && 
              (program.program_of_work || program.name || program.title);

            if (!isValid) {
              console.warn("Invalid program detected:", program);
            }
            return isValid;
          });

      if (validPrograms.length === 0 && !customProgramsSkipped) {
        console.error("No valid programs after validation");
        throw new Error("No valid programs received from workflow");
      }

      // CRITICAL: Update state in the correct order using extracted strategy data
      console.log("🔧 Setting state with strategy data:", {
        shortSummary: strategyData.shortSummary,
        problemStatement: strategyData.problemStatement,
        goalStatement: strategyData.goalStatement,
        targetStatement: strategyData.targetStatement,
        objectivesCount: Array.isArray(strategyData.objectives) ? strategyData.objectives.length : 0,
        programsCount: validPrograms.length
      });

      if (this.setters.setPlanName && strategyData.planName) {
        this.setters.setPlanName(strategyData.planName);
      }
      this.setters.setShortSummary(strategyData.shortSummary);
      this.setters.setSelectedPrograms(validPrograms);
      this.setters.setFinalProgramSequence(validPrograms);
      this.setters.setTotalDuration(strategyData.totalDuration);
      this.setters.setProblemStatement(strategyData.problemStatement);
      this.setters.setGoalStatement(strategyData.goalStatement);
      this.setters.setTargetStatement(strategyData.targetStatement);
      this.setters.setObjectivesStatement(strategyData.objectives);
      this.setters.setDeliverablesStatement(strategyData.deliverables);
      this.setters.setMetricsStatement(strategyData.metrics);
      this.setters.setStakeholdersCustomersInvolvement(
        Array.isArray(strategyData.stakeholders)
          ? strategyData.stakeholders.join("\n")
          : strategyData.stakeholders || "Client stakeholder involvement will be determined during program execution."
      );
      if (this.setters.setExperienceDesignPrinciples) {
        this.setters.setExperienceDesignPrinciples(strategyData.experienceDesignPrinciples || "");
      }

      // Set completion states AFTER data is set
      this.setters.setIsCreatingStrategy(false);
      this.setters.setIsStrategyCreationMode(false);
      this.setters.setHasStrategyBeenCreated(true);
      this.setters.setPlanState("saving");

      // Set timestamp to prevent auto-save conflicts
      window.strategyCompletionTime = Date.now();
      window.strategyJustCompleted = true;
      setTimeout(() => {
        window.strategyJustCompleted = false;
      }, 20000);

      // Mark plan as initialized to prevent hook from overwriting
      this.setters.setPlanLoadInitialized(true);
      this.setters.setIsInitialLoad(false);

      // CRITICAL FIX: Call savePlan() directly with complete data payload
      // This ensures BOTH Decision Call and Full Strategy use the SAME unified save logic
      // Pass data directly instead of relying on React state setters to complete
      console.log("🔥 ComponentActionHandler: Calling savePlan() directly with complete workflow data");
      
      try {
        // Prepare complete data payload for savePlan (bypasses state setter race conditions)
        const directProgramData = {
          programs: validPrograms,
          finalSequence: validPrograms,
          strategyData: strategyData,
          deferSetCurrentPlanId: true, // set after PS save so Pulse doesn't fire before data is ready
        };
        
        const workflowContext = {
          enabledFeatures: data?.enabledFeatures || this.getters.enabledFeatures
        };
        
        console.log("🔥 Passing direct data to savePlan:", {
          programsCount: directProgramData.programs?.length,
          hasStrategyData: !!directProgramData.strategyData,
          enabledFeatures: workflowContext.enabledFeatures
        });
        
        // Call savePlan() directly with complete payload - no reliance on state
        const savedPlanId = data?.savedPlanId || await this.savePlan(directProgramData, workflowContext);

        if (savedPlanId) {
          console.log("✅ Strategy saved successfully with plan ID:", savedPlanId);
          this.setters.setPlanState("saved");
          this.setters.setIsSaved(true);

          // CRITICAL FIX: Link conversation to saved plan DIRECTLY here
          // This ensures conversation linking happens regardless of callback chain
          console.log("🔗 ComponentActionHandler: Linking conversation to plan", savedPlanId);
          try {
            const { default: chatStateManager } = await import('./ChatStateManager');
            await chatStateManager.updateConversationPlanId(savedPlanId);
            console.log("✅ ComponentActionHandler: Conversation linked to plan", savedPlanId);
          } catch (linkError) {
            console.error("❌ ComponentActionHandler: Failed to link conversation:", linkError);
          }

          // CRITICAL: Save product strategy to product_strategy_reports table
          // This persists the strategic outputs (design hypotheses, customer goals, etc.)
          // Only save if we have product strategy data from the workflow
          
          // Debug: Log what product strategy data is available
          console.log("🔍 Product Strategy Data Check:", {
            hasBriefDeconstruction: !!data?.briefDeconstruction,
            hasDesignHypotheses: !!data?.designHypotheses,
            hasCustomerGoals: !!data?.customerGoals,
            hasPainPoints: !!data?.painPoints,
            hasAudienceInsights: !!data?.audienceInsights,
            hasInformationAssessment: !!data?.informationAssessment,
            hasStrategicGuidance: !!data?.strategicGuidance,
            dataKeys: Object.keys(data || {}).slice(0, 20)
          });
          
          const hasProductStrategyData = data?.briefDeconstruction || data?.designHypotheses || 
            data?.customerGoals || data?.painPoints || data?.audienceInsights;
          
          if (hasProductStrategyData && !data?.savedPlanId) {
            console.log("💾 Saving product strategy to product_strategy_reports...");
            try {
              const productStrategyResponse = await makeAuthenticatedRequest("/api/product-strategy/save", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  planId: savedPlanId,
                  planName: data?.planName || strategyData.shortSummary?.substring(0, 50) || "Decision Call",
                  designChallenge: data?.designChallenge || this.getters.currentDesignChallenge,
                  briefDeconstruction: data?.briefDeconstruction || {},
                  informationAssessment: data?.informationAssessment || {},
                  strategicGuidance: data?.strategicGuidance || {},
                  audienceInsights: data?.audienceInsights || [],
                  designHypotheses: data?.designHypotheses || data?.designHypothesis || [],
                  customerGoals: data?.customerGoals || [],
                  painPoints: data?.painPoints || [],
                  unmetNeeds: data?.unmetNeeds || [],
                  designVision: data?.designVision || "",
                  competitivePositioning: data?.competitivePositioning || data?.competitiveUxPositioning || "",
                  experienceDesignPrinciples: data?.experienceDesignPrinciples || "",
                  successMetrics: data?.successMetrics || [],
                  strategicThemes: data?.strategicThemes || [],
                  opportunityAreas: data?.opportunityAreas || [],
                  strategicDeliverables: data?.strategicDeliverables || []
                })
              });
              
              if (productStrategyResponse && productStrategyResponse.success) {
                console.log("✅ Product strategy saved to product_strategy_reports:", productStrategyResponse);
                
                // CRITICAL FIX: Increment strategy version AFTER product strategy is saved
                // This triggers ProductStrategyView to remount and load the now-saved data
                if (this.setters.incrementStrategyVersion) {
                  this.setters.incrementStrategyVersion();
                  console.log("🔄 Strategy version incremented AFTER product strategy save - view will refresh");
                }
              } else {
                console.warn("⚠️ Product strategy save response:", productStrategyResponse);
                // Still increment version even on partial success so UI updates
                if (this.setters.incrementStrategyVersion) {
                  this.setters.incrementStrategyVersion();
                }
              }
            } catch (productStrategyError) {
              console.error("❌ Failed to save product strategy:", productStrategyError);
              // Don't throw - plan is already saved, this is supplementary
            }
          } else {
            console.log("ℹ️ No product strategy data to save (workflow may not have included product_strategy feature)");
            // Still increment version for any strategy updates (Brief tab, etc.)
            if (this.setters.incrementStrategyVersion) {
              this.setters.incrementStrategyVersion();
              console.log("🔄 Strategy version incremented (no product strategy data)");
            }
          }

          // Reveal tabs now that plan + PS are fully saved
          if (this.setters.setCurrentPlanId) {
            this.setters.setCurrentPlanId(savedPlanId);
          }
          if(data?.savedPlanId) {
            this.setters.setIsNewPlan?.(false);
            this.setters.navigateToSavedPlan?.(savedPlanId);
          }

          // Call onSaveComplete callback after successful save
          console.log("🔥🔥🔥 ComponentActionHandler: Checking onSaveComplete callback", {
            hasCallback: !!data?.onSaveComplete,
            callbackType: typeof data?.onSaveComplete,
            savedPlanId
          });
          if (data?.onSaveComplete) {
            console.log("✅ ComponentActionHandler: Calling onSaveComplete callback with plan ID:", savedPlanId);
            data.onSaveComplete(savedPlanId);
            console.log("✅ ComponentActionHandler: onSaveComplete callback returned");
          } else {
            console.error("❌❌❌ ComponentActionHandler: onSaveComplete callback is MISSING from data!");
            console.error("❌❌❌ data keys:", Object.keys(data || {}));
          }

          console.log("🎉 Unified workflow completed - Decision Call and Full Strategy use identical save logic!");
        } else {
          throw new Error("Save operation returned no plan ID");
        }
      } catch (saveError) {
        console.error("❌ Strategy save failed:", saveError);
        this.setters.setPlanState("error");
        this.setters.setIsSaved(false);

        if (data?.onSaveError) {
          data.onSaveError(saveError);
        }
      }

    } catch (error) {
      console.error("Error in strategy completion:", error);
      this.setters.setPlanState("error");
      this.setters.setIsSaved(false);

      if (data.onSaveError) {
        data.onSaveError(error);
      }
    }
  }

  async saveStrategyData(strategyData, validPrograms) {
    console.log("Saving complete strategy data:", {
      programCount: validPrograms.length,
      programNames: validPrograms.map(p => p.program_of_work || p.name).slice(0, 3),
      hasShortSummary: !!strategyData.shortSummary,
      hasProblemStatement: !!strategyData.problemStatement,
      hasGoalStatement: !!strategyData.goalStatement,
      hasTargetStatement: !!strategyData.targetStatement,
      strategyContentPreview: {
        shortSummary: strategyData.shortSummary?.substring(0, 50) + "...",
        problemStatement: strategyData.problemStatement?.substring(0, 50) + "...",
        goalStatement: strategyData.goalStatement?.substring(0, 50) + "..."
      }
    });

    try {
      // Wait a moment for React state to update
      await new Promise(resolve => setTimeout(resolve, 100));

      // CRITICAL FIX: Pass complete strategy data directly to savePlan
      console.log("🚀 Calling savePlan with complete strategy and program data");

      // Pass all strategy data directly to avoid React state timing issues
      const savedPlanId = await this.savePlan({
        programs: validPrograms,
        finalSequence: validPrograms,
        strategyData: strategyData
      });

      if (savedPlanId) {
        console.log("✅ Complete strategy data saved successfully with plan ID:", savedPlanId);
        this.setters.setPlanState("saved");
        this.setters.setIsSaved(true);

        // Call the success callback if provided
        if (strategyData.onSaveComplete) {
          strategyData.onSaveComplete();
        }

        console.log("🎉 Workflow fully completed including database save!");
      } else {
        throw new Error("Save operation returned no plan ID");
      }
    } catch (error) {
      console.error("❌ Strategy data save failed:", error);
      this.setters.setPlanState("error");
      this.setters.setIsSaved(false);

      // Call the error callback if provided
      if (strategyData.onSaveError) {
        strategyData.onSaveError(error);
      }
    }
  }

  async executeAction(actionData, planData, updatePlan, agentId = 'DESIGN_LEAD') {
    const { action_type, component_id, capability } = actionData;

    console.log(`🎯 ComponentActionHandler: Executing ${action_type} on ${component_id}`);

    // Validate capability before execution
    if (capability) {
      const canExecute = await this.validateCapability(agentId, capability);
      if (!canExecute) {
        throw new Error(`Agent ${agentId} cannot execute capability: ${capability}`);
      }
      console.log(`✅ Capability validation passed: ${agentId} can execute ${capability}`);
    }

    // Use capability registry if the action_type is a known capability
    if (this.capabilityRegistry.hasCapability(action_type)) {
      return await this.capabilityRegistry.executeCapability(action_type, actionData);
    }

    // Fallback for unknown action types
    throw new Error(`No handler found for action type: ${action_type}`);
  }

  async handleUpdatePrograms(data) {
    console.log("🎯 ComponentActionHandler: Updating programs with data:", data);

    try {
      if (this.planOperations && typeof this.planOperations.handleUpdatePrograms === 'function') {
        const result = await this.planOperations.handleUpdatePrograms(data);
        console.log("✅ Programs updated via PlanOperationsCapability:", result);
        return result;
      } else {
        console.error("❌ PlanOperationsCapability not available");
        return { success: false, message: "Plan operations not available" };
      }
    } catch (error) {
      console.error("❌ Error updating programs:", error);
      return { success: false, message: error.message };
    }
  }

  async handleUpdateStrategyComponent(data, action = null) {
    console.log("🎯 ComponentActionHandler: Updating strategy component with data:", data);
    console.log("🎯 ComponentActionHandler: Action context:", action);

    try {
      if (this.planOperations && typeof this.planOperations.handleUpdateStrategyComponent === 'function') {
        const result = await this.planOperations.handleUpdateStrategyComponent(data, action);
        console.log("✅ Strategy component updated via PlanOperationsCapability:", result);
        return result;
      } else {
        console.error("❌ PlanOperationsCapability not available for strategy component update");
        return { success: false, message: "Plan operations not available for strategy updates" };
      }
    } catch (error) {
      console.error("❌ Error updating strategy component:", error);
      return { success: false, message: error.message };
    }
  }

  // Expose capability registry methods for external access
  getAvailableCapabilities() {
    return this.capabilityRegistry.getAvailableCapabilities();
  }

  getCapabilityInfo() {
    return this.capabilityRegistry.getCapabilityInfo();
  }

  hasCapability(capabilityName) {
    return this.capabilityRegistry.hasCapability(capabilityName);
  }

  // Handle tool duration editing
  handleToolDurationEditing(data) {
    console.log("🔧 Handling tool duration editing:", data);

    try {
      const { programIndex, stage, toolIdentifier, duration } = data;

      if (typeof programIndex !== 'number' || programIndex < 0) {
        return { success: false, message: "Invalid program index" };
      }

      if (!stage || typeof stage !== 'string') {
        return { success: false, message: "Invalid stage name" };
      }

      if (!toolIdentifier) {
        return { success: false, message: "Invalid tool identifier" };
      }

      if (typeof duration !== 'number' || duration <= 0) {
        return { success: false, message: "Invalid duration" };
      }

      // Get current program sequence
      const currentSequence = this.getCurrentProgramSequence();
      if (!currentSequence || programIndex >= currentSequence.length) {
        return { success: false, message: "Program not found" };
      }

      const program = currentSequence[programIndex];
      if (!program.toolsByStage || !program.toolsByStage[stage]) {
        return { success: false, message: `Stage '${stage}' not found in program` };
      }

      const stageData = program.toolsByStage[stage];
      if (!stageData.tools) {
        return { success: false, message: "No tools found in stage" };
      }

      // Find the tool to modify
      let toolFound = false;
      stageData.tools.forEach(tool => {
        if (tool.tool === toolIdentifier || tool.name === toolIdentifier || 
            tool.id === toolIdentifier || tool.id === `tool-${toolIdentifier}`) {
          tool.duration = duration;
          toolFound = true;
          console.log(`✅ Updated tool '${toolIdentifier}' duration to ${duration} hours`);
        }
      });

      if (!toolFound) {
        return { success: false, message: `Tool '${toolIdentifier}' not found in stage '${stage}'` };
      }

      // Recalculate stage duration
      stageData.totalDuration = stageData.tools.reduce((total, tool) => total + (tool.duration || 1), 0);

      // Recalculate program duration
      program.totalProgramLength = Object.values(program.toolsByStage).reduce(
        (total, stage) => total + (stage.totalDuration || 0), 0
      );

      // Update the state
      this.updateProgramSequence(currentSequence);

      return { 
        success: true, 
        message: `Tool '${toolIdentifier}' duration updated to ${duration} hours`,
        updatedTool: toolIdentifier,
        newDuration: duration,
        programDuration: program.totalProgramLength
      };

    } catch (error) {
      console.error("Error handling tool duration editing:", error);
      return { success: false, message: error.message };
    }
  }

  // Handle modifying tool content (name, description, complexity, etc.)
  handleModifyToolsContent(data) {
    console.log("🔧 Handling modify tools content:", data);

    try {
      const { programIndex, stage, toolIdentifier, modifications } = data;

      if (typeof programIndex !== 'number' || programIndex < 0) {
        return { success: false, message: "Invalid program index" };
      }

      if (!stage || typeof stage !== 'string') {
        return { success: false, message: "Invalid stage name" };
      }

      if (!toolIdentifier) {
        return { success: false, message: "Invalid tool identifier" };
      }

      if (!modifications || typeof modifications !== 'object') {
        return { success: false, message: "Invalid modifications data" };
      }

      // Get current program sequence
      const currentSequence = this.getCurrentProgramSequence();
      if (!currentSequence || programIndex >= currentSequence.length) {
        return { success: false, message: "Program not found" };
      }

      const program = currentSequence[programIndex];
      if (!program.toolsByStage || !program.toolsByStage[stage]) {
        return { success: false, message: `Stage '${stage}' not found in program` };
      }

      const stageData = program.toolsByStage[stage];
      if (!stageData.tools) {
        return { success: false, message: "No tools found in stage" };
      }

      // Find the tool to modify
      let toolFound = false;
      let modifiedTool = null;

      stageData.tools.forEach(tool => {
        if (tool.tool === toolIdentifier || tool.name === toolIdentifier || 
            tool.id === toolIdentifier || tool.id === `tool-${toolIdentifier}`) {

          // Apply modifications
          if (modifications.name !== undefined) {
            tool.tool = modifications.name;
            tool.name = modifications.name;
          }
          if (modifications.description !== undefined) {
            tool.description = modifications.description;
          }
          if (modifications.complexity !== undefined) {
            tool.complexity = modifications.complexity;
          }
          if (modifications.duration !== undefined) {
            tool.duration = modifications.duration;
          }
          if (modifications.related_stage !== undefined) {
            tool.related_stage = modifications.related_stage;
          }

          toolFound = true;
          modifiedTool = { ...tool };
          console.log(`✅ Modified tool '${toolIdentifier}':`, modifications);
        }
      });

      if (!toolFound) {
        return { success: false, message: `Tool '${toolIdentifier}' not found in stage '${stage}'` };
      }

      // Recalculate stage duration if duration was changed
      if (modifications.duration !== undefined) {
        stageData.totalDuration = stageData.tools.reduce((total, tool) => total + (tool.duration || 1), 0);

        // Recalculate program duration
        program.totalProgramLength = Object.values(program.toolsByStage).reduce(
          (total, stage) => total + (stage.totalDuration || 0), 0
        );
      }

      // Update the state
      this.updateProgramSequence(currentSequence);

      return { 
        success: true, 
        message: `Tool '${toolIdentifier}' content updated successfully`,
        updatedTool: modifiedTool,
        modifications: modifications,
        programDuration: program.totalProgramLength
      };

    } catch (error) {
      console.error("Error handling modify tools content:", error);
      return { success: false, message: error.message };
    }
  }

  getCurrentProgramSequence() {
    return this.getters.getFinalProgramSequence();
  }

  updateProgramSequence(newSequence) {
    this.setters.setFinalProgramSequence(newSequence);
    this.setters.setSelectedPrograms(newSequence);
    this.savePlan();
  }
}

module.exports = ComponentActionHandler;
